/**
 * Unified Home Page - Dynamic Menu System
 * All users see this page after login with dynamic menus from API
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, Routes, Route } from 'react-router-dom';
import { Home, ChevronRight, LogOut, Menu, X, Settings, User, Key } from 'lucide-react';
import logo from '../assets/images/logo.jpg';
import Footer from '../components/layout/Footer';
import NotificationBell from '../components/ui/NotificationBell';
import { useAuth } from '../auth/AuthContext';
import authService from '../api/authService';
import { performLogout } from '../services/logoutService';
import { useDynamicMenu } from '../hooks/useDynamicMenu';
import { getComponent, getComponentByRoute } from '../config/ComponentRegistry';
import { getComponentByRoutePath, CENTRALIZED_ROUTE_MAP } from '../config/CentralizedRoutes';
import LandingChatbot from '../components/chatbot/LandingChatbot';
import ChangePasswordMenuItem from '../components/auth/ChangePasswordMenuItem';
import useChangePasswordModal from '../hooks/useChangePasswordModal';
import useFeaturePermission from '../components/globalfeatures';
import AgentGlobalChatDock from '../components/chat/AgentGlobalChatDock';
import FeedbackPopup from '../components/feedback/FeedbackPopup';
import { ticketService } from '../api/ticketService';

/** Normalize GET /feedback/closed-without-feedback wrapped API shapes */
function parseClosedTicketsWithoutFeedback(response) {
  let tickets = [];
  if (!response?.data) return tickets;
  const rb = response.data;
  const ok =
    rb.success === true ||
    rb.success === 'true' ||
    rb.status === 200 ||
    rb.status === '200';
  if (ok && Array.isArray(rb.data)) {
    tickets = rb.data;
  } else if (Array.isArray(rb)) {
    tickets = rb;
  } else if (rb.data && Array.isArray(rb.data)) {
    tickets = rb.data;
  }
  return tickets;
}

function screenvalidation(screen) {
  const hasPermission = [
    screen?.canView,
    screen?.canUpdate,
    screen?.canInsert,
    screen?.canDelete
  ].some(Boolean);

  // return screen?.isActive === true && hasPermission;
  return hasPermission;
}

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [expandedItems, setExpandedItems] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentComponent, setCurrentComponent] = useState(null);
  const [currentScreen, setCurrentScreen] = useState(null);

  const enableChatbot = useFeaturePermission('enable_chatbot');

  const [closedTicketsWithoutFeedback, setClosedTicketsWithoutFeedback] = useState([]);
  const [currentFeedbackTicketIndex, setCurrentFeedbackTicketIndex] = useState(0);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);

  const settingsRef = useRef(null);
  const sidebarRef = useRef(null);
  const { openChangePasswordModal, changePasswordModal } = useChangePasswordModal();
  const { menuData, loading: menuLoading, error: menuError } = useDynamicMenu();
  
  const currentUser = authService.getCurrentUser();
  const displayName = sessionStorage.getItem('userName') || currentUser?.username || currentUser?.email || 'User';
  const displayRole = currentUser?.roleCode || currentUser?.role || '';
  const [displayTeamName, setDisplayTeamName] = useState('');
  
  // Check if user is a customer/end-user
  const isCustomer = displayRole?.toLowerCase() === 'customer' || 
                     displayRole?.toLowerCase() === 'end-user' ||
                     displayRole?.toLowerCase() === 'enduser' ||
                     currentUser?.role?.toLowerCase() === 'customer' ||
                     currentUser?.role?.toLowerCase() === 'end-user' ||
                     currentUser?.role?.toLowerCase() === 'enduser';

  const runClosedTicketFeedbackCheck = useCallback(async () => {
    if (!isCustomer) return;
    const token = authService.getToken();
    if (!token) return;
    try {
      const response = await ticketService.getClosedTicketsWithoutFeedback();
      const tickets = parseClosedTicketsWithoutFeedback(response);
      if (Array.isArray(tickets) && tickets.length > 0) {
        setClosedTicketsWithoutFeedback(tickets);
        setCurrentFeedbackTicketIndex(0);
        setShowFeedbackPopup(true);
      }
    } catch (error) {
      console.error('Error checking for closed tickets without feedback:', error);
    }
  }, [isCustomer]);

  useEffect(() => {
    const updateUserProfileMeta = () => {
      const teamName = authService.getStoredUserTeamName();
      setDisplayTeamName((prev) => (prev !== teamName ? teamName : prev));
    };
    updateUserProfileMeta();
    const handleProfileLogin = () => setTimeout(updateUserProfileMeta, 100);
    window.addEventListener('user-logged-in', handleProfileLogin);
    const interval = setInterval(updateUserProfileMeta, 500);
    return () => {
      window.removeEventListener('user-logged-in', handleProfileLogin);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!isCustomer) return undefined;
    runClosedTicketFeedbackCheck();
    const handleLogin = () => {
      setTimeout(runClosedTicketFeedbackCheck, 200);
    };
    window.addEventListener('user-logged-in', handleLogin);
    return () => window.removeEventListener('user-logged-in', handleLogin);
  }, [isCustomer, user, runClosedTicketFeedbackCheck]);

  const handleFeedbackSubmitted = (ticketId) => {
    setClosedTicketsWithoutFeedback((prev) => {
      const next = prev.filter((t) => t.ticketId !== ticketId);
      if (next.length === 0) {
        setShowFeedbackPopup(false);
      }
      setCurrentFeedbackTicketIndex(0);
      return next;
    });
  };

  const handleFeedbackClose = () => {
    setShowFeedbackPopup(false);
  };

  // Icon mapping - matches API module names
  const getIcon = (moduleName) => {
    const icons = {
      'Dashboard': Home,
      'Users': User,
      'Manage': Settings,
      'Workflow': Menu,
      'Reports': Settings,
      'Settings': Settings,
      'Knowledge': Settings,
      'Security': Settings,
      'Administration': Settings,
      'Tickets': Menu,
      'Integrated Module': Menu,
    };
    return icons[moduleName] || Settings;
  };

  // Find dashboard screen
  const findDashboard = () => {
    for (const module of menuData) {
      if (!module.isActive) continue;
      for (const screen of module.submenu || []) {
        if (screen.isDashboard && screen.isActive && screenvalidation(screen)) {
          return screen;
        }
      }
    }
    // Return first active screen if no dashboard found
    for (const module of menuData) {
      if (!module.isActive) continue;
      for (const screen of module.submenu || []) {
        if (screen.isActive && screenvalidation(screen)) {
          return screen;
        }
      }
    }
    return null;
  };

  // Find screen by route
  const findScreenByRoute = (path) => {
    if (!path || !menuData || menuData.length === 0) {
      //console.log('⚠️ findScreenByRoute: Invalid path or menu data');
      return null;
    }
    
    //console.log('🔍 Finding screen for path:', path);
    
    // Normalize path (remove trailing slashes, ensure it starts with /)
    const normalizedPath = path.replace(/\/+$/, '') || '/';
    
    for (const module of menuData) {
      if (!module.isActive) {
        //console.log(`⏭️ Skipping inactive module: ${module.menumoduleName}`);
        continue;
      }
      
      if (!module.submenu || module.submenu.length === 0) {
        //console.log(`⏭️ Module has no submenu: ${module.menumoduleName}`);
        continue;
      }
      
      //console.log(`📁 Checking module: ${module.menumoduleName} (${module.submenu.length} screens)`);
      
      for (const screen of module.submenu) {
        // Check if screen is active and viewable
        if (screen.isActive !== true) {
          //console.log(`  ⏭️ Skipping inactive screen: ${screen.screenName}`);
          continue;
        }
        
        if (!screenvalidation(screen)) {
          //console.log(`  ⏭️ Skipping screen without canView: ${screen.screenName}`);
          continue;
        }
        
        if (!screen.routing) {
          //console.log(`  ⏭️ Skipping screen without routing: ${screen.screenName}`);
          continue;
        }
        
        // Normalize screen route
        const screenRoute = screen.routing.replace(/\/+$/, '') || '/';
        
        //console.log(`  📄 Screen: "${screen.screenName}", Route: "${screenRoute}"`);
        
        // Exact match
        if (screenRoute === normalizedPath) {
          //console.log(`  ✅ Exact match found: ${screen.screenName}`);
          return screen;
        }
        
        // Path starts with screen route (for nested routes like /admin/administration/modules/edit/123)
        if (normalizedPath.startsWith(screenRoute + '/')) {
          //console.log(`  ✅ Path starts with route: ${screen.screenName}`);
          return screen;
        }
        
        // Screen route starts with path (for parent routes)
        if (screenRoute.startsWith(normalizedPath + '/')) {
          //console.log(`  ✅ Route starts with path: ${screen.screenName}`);
          return screen;
        }
        
        // Case-insensitive comparison (as fallback)
        if (screenRoute.toLowerCase() === normalizedPath.toLowerCase()) {
          //console.log(`  ✅ Case-insensitive match found: ${screen.screenName}`);
          return screen;
        }
      }
    }
    
    //console.log('❌ No screen found for path:', normalizedPath);
    //console.log('📋 Available routes:', menuData.flatMap(m => 
    //   m.submenu?.filter(s => s.isActive && screenvalidation(s)).map(s => s.routing) || []
    // ));
    return null;
  };

  // Public routes that should redirect to landing
  const publicRoutes = ['/', '/landing', '/signin', '/login', '/dashboard', '/public-dashboard', '/landing-old', '/privacy', '/privacy-policy'];

  // Resolve component for current route
  useEffect(() => {
    const path = location.pathname;
    
    // Handle public routes
    if (publicRoutes.includes(path)) {
      if (!user && !authService.isAuthenticated()) {
        // Already on public route, do nothing
        return;
      }
    }
    
    // Redirect unauthenticated users to signin
    // BUT: Allow portal routes to be checked first in case auth state is temporarily unavailable
    const isAuthenticated = authService.isAuthenticated();
    const hasToken = !!sessionStorage.getItem('token') || !!sessionStorage.getItem('authToken');
    
    // Only redirect if truly unauthenticated (no user, no token, not authenticated)
    // This prevents redirects during auth state transitions
    if (!user && !isAuthenticated && !hasToken) {
      if (!publicRoutes.includes(path)) {
        navigate('/signin', { replace: true });
      }
      return;
    }

    // FIRST: Check if route exists in CENTRALIZED_ROUTE_MAP (exact match takes priority)
    // This ensures routes like /portal/tickets/create are found before /portal/tickets
    // IMPORTANT: Check this BEFORE menu loading checks, so routes work even when menu is loading
    
    // Direct exact match check first (fastest)
    const directMatch = CENTRALIZED_ROUTE_MAP[path];
    if (directMatch) {
      setCurrentComponent(() => directMatch);
      return; // Exit early
    }
    
    // If no direct match, use getComponentByRoutePath for pattern matching
    const centralizedRouteComponent = getComponentByRoutePath(path);
    if (centralizedRouteComponent) {
      // Check if it's a dynamic route (contains :id, :slug, etc.)
      const dynamicRoutePattern = Object.keys(CENTRALIZED_ROUTE_MAP).find(route => {
        if (route.includes(':')) {
          const routePattern = route.replace(/:[^/]+/g, '[^/]+');
          const regex = new RegExp(`^${routePattern}$`);
          return regex.test(path);
        }
        return false;
      });
      
      if (dynamicRoutePattern) {
        // For dynamic routes, wrap in Routes/Route to provide useParams context
        setCurrentComponent(() => {
          const DynamicRouteWrapper = () => {
            return (
              <Routes location={location}>
                <Route path={dynamicRoutePattern} element={React.createElement(centralizedRouteComponent)} />
              </Routes>
            );
          };
          return DynamicRouteWrapper;
        });
      } else {
        // Static route - render component directly
        setCurrentComponent(() => centralizedRouteComponent);
      }
      return; // Exit early - don't check menu
    }

    // SECOND: If not in CENTRALIZED_ROUTE_MAP, check menu-based routing
    // But only if menu is loaded
    if (menuLoading) {
      //console.log('⏳ Menu is loading...');
      return;
    }

    if (menuData.length === 0) {
      //console.log('⚠️ No menu data available');
      return;
    }
    //console.log('📍 Current path:', path);
    //console.log('📊 Menu data count:', menuData.length);

    // If at /home, redirect to dashboard
    if (path === '/home' || path === '/home/') {
      const dashboard = findDashboard();
      if (dashboard?.routing) {
        //console.log('🏠 Redirecting to dashboard:', dashboard.routing);
        navigate(dashboard.routing, { replace: true });
        return;
      }
    }

    // SECOND: If not in CENTRALIZED_ROUTE_MAP, check menu-based routing
    //console.log('⚠️ Route not in CENTRALIZED_ROUTE_MAP, checking menu...');
    const screen = findScreenByRoute(path);
    //console.log('🔍 Screen found for route:', screen ? screen.screenName : 'None');
    //console.log('📍 Current path being resolved:', path);

    if (screen) {
      setCurrentScreen(screen);
      // Pass route context to getComponent to distinguish portal vs agent
      let Component = getComponent(screen.screenName, screen.routing);
      //console.log('🔧 Component by screenName (with route context):', screen.screenName, screen.routing, Component ? '✅ Found' : '❌ Not found');
      //console.log('🔧 menu:', screen);
      sessionStorage.setItem('menu', JSON.stringify(screen));
      
      if (!Component) {
        Component = getComponentByRoute(screen.routing);
        //console.log('🔧 Component by route:', screen.routing, Component ? '✅ Found' : '❌ Not found');
      }
      
      if (!Component) {
        console.warn('⚠️ Component not found for:', screen.screenName, screen.routing);
        Component = () => (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Screen Not Found</h2>
            <p>Component not found for: {screen.screenName}</p>
            <p className="text-sm text-gray-500 mt-2">Route: {screen.routing}</p>
          </div>
        );
      }
      setCurrentComponent(() => Component);
    } else {
      // Route not in menu and not in CENTRALIZED_ROUTE_MAP
      //console.log('⚠️ Route not found in menu:', path);
      //console.log('📋 Available portal routes:', Object.keys(CENTRALIZED_ROUTE_MAP).filter(r => r.startsWith('/portal')).slice(0, 10));
      
      // For portal routes, try one more time with exact match
      if (path.startsWith('/portal')) {
        const exactMatch = CENTRALIZED_ROUTE_MAP[path];
        if (exactMatch) {
          //console.log('✅ Found portal route via exact match:', path);
          setCurrentComponent(() => exactMatch);
          return;
        }
      }
      
      // Redirect to dashboard if route not found
      //console.log('⚠️ Route not found, redirecting to dashboard');
      const dashboard = findDashboard();
      if (dashboard?.routing) {
        navigate(dashboard.routing, { replace: true });
      } else {
        setCurrentComponent(() => () => (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Welcome</h2>
            <p>Select a menu item to get started.</p>
            <p className="text-sm text-gray-500 mt-2">Route not found: {path}</p>
          </div>
        ));
      }
    }
  }, [location.pathname, menuData, menuLoading, navigate, user]);

  // Auto-expand active menu (only if not explicitly set by user)
  useEffect(() => {
    if (menuData.length === 0) return;
    setExpandedItems(prev => {
      const newExpanded = { ...prev };
      menuData.forEach(module => {
        // Only auto-expand if not explicitly set by user
        if (prev[module.menumoduleName] === undefined) {
          const hasActiveChild = module.submenu?.some(screen => {
            const route = screen.routing;
            return location.pathname === route || location.pathname.startsWith(route + '/');
          });
          if (hasActiveChild) {
            newExpanded[module.menumoduleName] = true;
          }
        }
      });
      return newExpanded;
    });
  }, [location.pathname, menuData]);

  // Close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && 
          !event.target.closest('[data-sidebar-toggle]')) {
        if (window.innerWidth < 1024) {
          setSidebarOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => performLogout({ navigate, reason: 'manual' });

  const toggleExpanded = (moduleName) => {
    setExpandedItems(prev => ({
      ...prev,
      [moduleName]: !prev[moduleName]
      
    }));
    //console.log('🔍 Expanded items:', expandedItems);
  };

  const renderComponent = () => {
    if (menuLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#006600]"></div>
        </div>
      );
    }
    if (menuError) {
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-red-800 font-semibold mb-2">Error Loading Menu</h3>
            <p className="text-red-600">{menuError}</p>
          </div>
        </div>
      );
    }
    if (!currentComponent) {
      return (
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4">Loading...</h2>
        </div>
      );
    }

    const Component = currentComponent;
    const permissions = currentScreen ? {
      canView: currentScreen.canView,
      canInsert: currentScreen.canInsert,
      canUpdate: currentScreen.canUpdate,
      canDelete: currentScreen.canDelete,
    } : {};

    return <Component {...permissions} />;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Matching old AdminLayout style */}
      {/* <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-border shadow-sm"> */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-border shadow-sm sidebar-header-strip">
        <div className="w-full">
          <div className="flex items-stretch h-14 sm:h-16">
            {/* Left strip (joins sidebar) - User info section */}
            {/* <div className=" hidden lg:flex w-64 bg-[#0B2530] border-r border-[#0B2530] items-center px-4 gap-3"> */}
            <div className=" sidebar-header-strip hidden lg:flex w-64 bg-[#0B2530] border-r border-[#0B2530] items-center px-4 gap-3">
              <img
                src={logo}
                alt="Logo"
                className="h-10 w-10 rounded-full object-cover"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white truncate" title={displayName}>{displayName}</div>
                {displayRole && (
                  <div
                    className="text-xs truncate leading-snug"
                    title={displayTeamName ? `${displayRole} · ${displayTeamName}` : displayRole}
                  >
                    <span className="font-medium uppercase tracking-wide text-white/70">{displayRole}</span>
                    {displayTeamName ? (
                      <>
                        <span className="mx-1.5 text-white/30" aria-hidden="true">·</span>
                        <span className="font-normal text-white/50">{displayTeamName}</span>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="ml-auto shrink-0 p-2 text-white/80 hover:text-white rounded-md transition-colors"
                aria-label="Toggle sidebar"
              >
                <Menu size={20} />
              </button>
            </div>

            {/* Right strip (navbar) */}
            <div className="flex-1 flex items-center px-4 sm:px-6 lg:px-8">
              {/* Mobile Menu Button */}
              <button
                data-sidebar-toggle
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 text-muted-foreground hover:text-foreground hover:bg-[#F8FCFB] rounded-md transition-colors"
                aria-label="Toggle menu"
              >
                <Menu size={20} />
              </button>

              <h1 className="text-lg sm:text-xl font-bold text-foreground ml-2 sm:ml-0">
                HelpDesk
              </h1>

              <div className="flex items-center space-x-1 sm:space-x-2 ml-auto">
                {/* Notifications */}
                <NotificationBell menuData={menuData} />

                {/* Settings */}
                <div className="relative" ref={settingsRef}>
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-[#F8FCFB] rounded-md transition-colors"
                    title="Settings"
                  >
                    <Settings size={20} />
                  </button>

                  {/* Settings Dropdown */}
                  {showSettings && (
                    <div className="absolute right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-border dark:border-gray-700 z-50">
                      <div className="p-2">
                        <button
                          onClick={() => {
                            navigate('/profile');
                            setShowSettings(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-[#0B2530] dark:text-gray-300 hover:bg-[#F8FCFB] dark:hover:bg-gray-700 rounded-md flex items-center gap-2"
                        >
                          <User size={16} />
                          Profile
                        </button>
                        <ChangePasswordMenuItem
                          onOpen={openChangePasswordModal}
                          onClose={() => setShowSettings(false)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-[#F8FCFB] rounded-md transition-colors"
                  title="Logout"
                >
                  <LogOut size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex relative min-h-[calc(100dvh-3.5rem)] pt-14 sm:min-h-[calc(100dvh-4rem)] sm:pt-16">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - Matching old AdminLayout style */}
        <nav 
          ref={sidebarRef}
          className={`
            sidebar-nav
            fixed lg:fixed left-0 z-50
            top-14 sm:top-16 lg:top-14 sm:lg:top-16
            w-64 bg-[#0B2530] border-r border-[#0B2530]
            transform transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            ${sidebarCollapsed ? 'lg:-translate-x-full' : 'lg:translate-x-0'}
            h-[calc(100vh-3.5rem)] sm:h-[calc(100vh-4rem)] lg:h-[calc(100vh-3.5rem)] sm:lg:h-[calc(100vh-4rem)]
            flex flex-col
            overflow-hidden
          `}
        >
          {/* Close button for mobile - Fixed at top */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 lg:hidden flex-shrink-0">
            <h2 className="text-lg font-semibold text-white">Menu</h2>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-md"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Scrollable menu content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="p-4">
              <div className="space-y-2">
              {menuLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                </div>
              ) : menuError ? (
                <div className="text-red-200 text-sm px-3 py-2">
                  <p className="font-semibold mb-1">Error loading menu:</p>
                  <p className="text-xs">{menuError}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-2 px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-xs"
                  >
                    Retry
                  </button>
                </div>
              ) : menuData.length > 0 ? (
                (() => {
                  // Filter modules: only active modules with screens
                  const filteredModules = menuData
                    .filter(module => {
                      if (module.isActive !== true) {
                        //console.log(`⏭️ Skipping inactive module: ${module.menumoduleName}`);
                        return false;
                      }
                      if (!module.submenu || module.submenu.length === 0) {
                        //console.log(`⏭️ Skipping module without screens: ${module.menumoduleName}`);
                        return false;
                      }
                      const hasVisibleScreens = module.submenu.some(screen => 
                        screen.isActive === true && screenvalidation(screen)
                      );
                      if (!hasVisibleScreens) {
                        //console.log(`⏭️ Skipping module with no visible screens: ${module.menumoduleName}`);
                        return false;
                      }
                      //console.log(`✅ Showing module: ${module.menumoduleName} with ${module.submenu.length} screens`);
                      return true;
                    });

                  if (filteredModules.length === 0) {
                    return (
                      <div className="text-white/60 text-sm px-3 py-2">
                        <p>No menu items available</p>
                        <p className="text-xs mt-1">Total modules from API: {menuData.length}</p>
                      </div>
                    );
                  }

                  return filteredModules.map((module) => {
                    const Icon = getIcon(module.menumoduleName);
                    // Get only active screens with canView permission
                    const visibleScreens = module.submenu.filter(screen => 
                      screen.isActive === true && screenvalidation(screen)
                    );

                    // Single visible screen → direct nav item (no expandable parent)
                    if (visibleScreens.length === 1) {
                      const screen = visibleScreens[0];
                      const isActive = location.pathname === screen.routing ||
                        location.pathname.startsWith(screen.routing + '/');
                      const label =
                        screen.isDashboard === true
                          ? 'Dashboard'
                          : (screen.screenLable || screen.screenName);
                      const isExternalLogin =
                        screen.screenLable === 'Reference Customer Login' ||
                        screen.screenName === 'Reference Customer Login';

                      return (
                        <div key={module.id || module.menumoduleName}>
                          <button
                            onClick={() => {
                              if (isExternalLogin) {
                                window.open('https://bedev.samvaadpro.com/', '_blank', 'noopener,noreferrer');
                                return;
                              }
                              navigate(screen.routing);
                            }}
                            className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                              isActive
                                ? 'bg-[#006600] text-white'
                                : 'text-white/90 hover:bg-[#006600] hover:text-white'
                            }`}
                          >
                            <Icon size={18} className="mr-3" />
                            {label}
                          </button>
                        </div>
                      );
                    }

                    const hasActiveChild = visibleScreens.some(screen => {
                      const route = screen.routing;
                      return location.pathname === route || location.pathname.startsWith(route + '/');
                    });
                    // If explicitly set in expandedItems, use that; otherwise default to hasActiveChild
                    const isExpanded = expandedItems[module.menumoduleName] !== undefined 
                      ? expandedItems[module.menumoduleName] 
                      : hasActiveChild;
                    
                    return (
                      <div key={module.id || module.menumoduleName}>
                        <button
                          onClick={() => toggleExpanded(module.menumoduleName)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            hasActiveChild
                              ? 'bg-[#006600] text-white'
                              : 'text-white/90 hover:bg-[#006600] hover:text-white'
                          }`}
                        >
                          <div className="flex items-center">
                            <Icon size={18} className="mr-3" />
                            {module.menumoduleName}
                          </div>
                          <ChevronRight 
                            size={16} 
                            className={`transition-transform ${
                              isExpanded ? 'rotate-90' : ''
                            }`} 
                          />
                        </button>
                        {isExpanded && (
                          <div className="ml-6 mt-1 space-y-1">
                            {visibleScreens.map((screen) => {
                              const isActive = location.pathname === screen.routing || 
                                            location.pathname.startsWith(screen.routing + '/');
                              
                              // Special handling for Reference Customer Login
                              if (screen.screenLable === 'Reference Customer Login' || screen.screenName === 'Reference Customer Login') {
                                return (
                                  <button
                                    key={screen.id || screen.routing}
                                    onClick={() => {
                                      window.open('https://bedev.samvaadpro.com/', '_blank', 'noopener,noreferrer');
                                    }}
                                    className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                                      isActive
                                        ? 'bg-[#006600] text-white font-medium'
                                        : 'text-white/80 hover:bg-[#006600] hover:text-white'
                                    }`}
                                  >
                                    {screen.screenLable || screen.screenName}
                                  </button>
                                );
                              }
                              
                              return (
                                <button
                                  key={screen.id || screen.routing}
                                  onClick={() => {
                                    //console.log('🖱️ Navigating to:', screen.routing);
                                    navigate(screen.routing);
                                  }}
                                  className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                                    isActive
                                      ? 'bg-[#006600] text-white font-medium'
                                      : 'text-white/80 hover:bg-[#006600] hover:text-white'
                                  }`}
                                >
                                  {screen.screenLable || screen.screenName}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  });
              })()
            ) : (
              <div className="text-white/60 text-sm px-3 py-2">
                <p>No menu items available</p>
                <p className="text-xs mt-1">Menu data is empty. Check API response.</p>
              </div>
            )}
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main
          data-modal-scroll-lock
          className={`flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-all duration-300 ${
            sidebarCollapsed ? 'lg:ml-0' : 'lg:ml-64'
          }`}
        >
          <div className="w-full min-w-0 max-w-none px-3 py-3 sm:px-4 sm:py-4 lg:px-6 lg:py-5">
            {renderComponent()}
          </div>
          <Footer />
        </main>
      </div>
      
      {authService.isAuthenticated() && enableChatbot && (
        <LandingChatbot
          email={currentUser?.email || currentUser?.username || undefined}
        />
      )}

      {authService.isAuthenticated() && <AgentGlobalChatDock />}

      {isCustomer && showFeedbackPopup && closedTicketsWithoutFeedback.length > 0 && (
        <FeedbackPopup
          ticket={closedTicketsWithoutFeedback[currentFeedbackTicketIndex]}
          onClose={handleFeedbackClose}
          onSubmitted={handleFeedbackSubmitted}
        />
      )}

      {changePasswordModal}
    </div>
  );
};

export default HomePage;
